/**
 * Mega Menu Item — frontend view script.
 *
 * • CSS handles base hover via :hover / :focus-within.
 * • JS adds `.is-active` with a 150ms grace-period so the panel stays
 *   open while the cursor crosses the gap between the trigger and the
 *   fixed-position dropdown panel.
 * • When any dropdown is open, `mega-menu-is-open` is added to the
 *   nearest <header> ancestor and to <body>.
 */

const HOVER_DELAY   = 150; // ms grace period between item ↔ panel
const OPEN_CLASS    = 'mega-menu-is-open';

// Best-effort header element — covers block-theme template parts and classic themes.
const siteHeader =
    document.querySelector( 'header.wp-block-template-part' ) ||
    document.querySelector( 'header[role="banner"]' )          ||
    document.querySelector( 'header' );

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Count currently active (open) dropdowns and sync the header class. */
function syncHeaderClass() {
    const anyOpen = !! document.querySelector(
        '.wp-block-jormi-blocks-mega-menu-item.is-active, ' +
        '.mega-menu__link.has-dropdown[aria-expanded="true"]'
    );

    document.body.classList.toggle( OPEN_CLASS, anyOpen );
    siteHeader?.classList.toggle( OPEN_CLASS, anyOpen );
}

/**
 * Close all open dropdowns, optionally skipping one link element.
 *
 * @param {Element|null} exceptLink Link to leave untouched.
 */
function closeAll( exceptLink = null ) {
    document
        .querySelectorAll( '.mega-menu__link.has-dropdown[aria-expanded="true"]' )
        .forEach( ( openLink ) => {
            if ( openLink === exceptLink ) return;
            openLink.setAttribute( 'aria-expanded', 'false' );
            const parentItem = openLink.closest(
                '.wp-block-jormi-blocks-mega-menu-item'
            );
            parentItem
                ?.querySelector( '.mega-menu__dropdown' )
                ?.setAttribute( 'aria-hidden', 'true' );
            parentItem?.classList.remove( 'is-active' );
        } );

    syncHeaderClass();
}

// ─── Per-item setup ────────────────────────────────────────────────────────

document.addEventListener( 'DOMContentLoaded', () => {
    const menuItems = document.querySelectorAll(
        '.wp-block-jormi-blocks-mega-menu-item'
    );

    menuItems.forEach( ( item ) => {
        const link     = item.querySelector( '.mega-menu__link.has-dropdown' );
        const dropdown = item.querySelector( '.mega-menu__dropdown' );

        if ( ! link || ! dropdown ) return;

        let hideTimer = null;

        // ── Open ──────────────────────────────────────────────────
        const openDropdown = () => {
            clearTimeout( hideTimer );
            item.classList.add( 'is-active' );
            dropdown.setAttribute( 'aria-hidden', 'false' );
            syncHeaderClass();
        };

        // ── Schedule close (grace period) ────────────────────────
        const scheduleClose = () => {
            clearTimeout( hideTimer );
            hideTimer = setTimeout( () => {
                if ( link.getAttribute( 'aria-expanded' ) !== 'true' ) {
                    item.classList.remove( 'is-active' );
                    dropdown.setAttribute( 'aria-hidden', 'true' );
                    syncHeaderClass();
                }
            }, HOVER_DELAY );
        };

        // ── Hover: item ───────────────────────────────────────────
        item.addEventListener( 'mouseenter', openDropdown );
        item.addEventListener( 'mouseleave', scheduleClose );

        // ── Hover: panel (cancels the grace-period timer) ─────────
        // The panel is position:fixed so the cursor leaves the item's
        // normal-flow bounding box before arriving at the panel.
        dropdown.addEventListener( 'mouseenter', openDropdown );
        dropdown.addEventListener( 'mouseleave', scheduleClose );

        // ── Click / tap toggle ────────────────────────────────────
        link.addEventListener( 'click', ( e ) => {
            const isExpanded = link.getAttribute( 'aria-expanded' ) === 'true';

            closeAll( link );

            const nowExpanded = ! isExpanded;
            link.setAttribute( 'aria-expanded', String( nowExpanded ) );
            dropdown.setAttribute( 'aria-hidden', String( ! nowExpanded ) );

            if ( nowExpanded ) {
                item.classList.add( 'is-active' );
                e.preventDefault(); // suppress navigation when opening
            } else {
                item.classList.remove( 'is-active' );
            }

            syncHeaderClass();
        } );

        // ── Escape key ────────────────────────────────────────────
        item.addEventListener( 'keydown', ( e ) => {
            if ( e.key === 'Escape' ) {
                link.setAttribute( 'aria-expanded', 'false' );
                dropdown.setAttribute( 'aria-hidden', 'true' );
                item.classList.remove( 'is-active' );
                link.focus();
                syncHeaderClass();
            }
        } );
    } );

    // ── Click outside ─────────────────────────────────────────────
    document.addEventListener( 'click', ( e ) => {
        if ( ! e.target.closest( '.wp-block-jormi-blocks-mega-menu-item' ) ) {
            closeAll();
        }
    } );
} );
